import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService, SELECTED_LANG } from './local-storage.service';

export type SupportedLanguage = 'nl' | 'nl-be' | 'en';

const FALLBACK_LANGUAGE: SupportedLanguage = 'nl';

const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ['nl', 'nl-be', 'en'];

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly localStorageService = inject(LocalStorageService);

  public readonly supportedLanguages: readonly SupportedLanguage[] = SUPPORTED_LANGUAGES;
  public readonly fallbackLanguage: SupportedLanguage = FALLBACK_LANGUAGE;

  public initialize(userId?: string): void {
    this.translate.addLangs([...this.supportedLanguages]);
    this.translate.setFallbackLang(this.fallbackLanguage);

    const userLang = userId
      ? this.localStorageService.getUserScopedItem(SELECTED_LANG, userId)
      : null;
    const globalLang = this.localStorageService.getPersistentItem(SELECTED_LANG);
    const lang = this.isSupportedLanguage(userLang)
      ? userLang
      : this.isSupportedLanguage(globalLang)
        ? globalLang
        : this.fallbackLanguage;

    this.translate.use(lang);
    this.localStorageService.setPersistentItem(SELECTED_LANG, lang);
  }

  public setLanguage(language: SupportedLanguage, userId?: string): void {
    this.translate.use(language);
    this.localStorageService.setPersistentItem(SELECTED_LANG, language);
    if (userId) {
      this.localStorageService.setUserScopedItem(SELECTED_LANG, language, userId);
    }
  }

  public isSupportedLanguage(language: string | null): language is SupportedLanguage {
    return (
      !!language &&
      this.supportedLanguages.some((supportedLanguage) => supportedLanguage === language)
    );
  }
}
